import Phaser from "phaser";
import {
  Direction,
  DIRECTION_FRAMES,
  Layer,
  PREVIEW_FRAME_DOWN,
  partKey,
  loadParts,
  loadManifest,
  walkFrames,
} from "./atlas";

/**
 * LimeZu-layered character avatar.
 *
 * Replaces the procedural `CharacterAvatar` with a 6-layer sprite stack:
 *   body  →  eyes  →  outfit  →  hair  →  headgear  →  accessory
 *
 * Each layer is a Phaser.GameObjects.Sprite drawn at the same world (x, y)
 * with the same z-depth; only the rendered FRAME differs per layer (each
 * layer's spritesheet has the same grid layout, so frame N looks identical
 * across body/eyes/hair/etc).
 *
 * MVP: uses static "preview" frames per direction (no animation). The frame
 * selection logic is in DIRECTION_FRAMES at atlas.ts; later phases can
 * replace setFrame() calls with Phaser anim playback.
 *
 * Headgear is special: it's a 32×32 single image (not a 1792×1312 spritesheet),
 * so it draws as a Phaser.GameObjects.Image positioned over the head region.
 */

const DEFAULT_DEPTH = 100;
/** Display scale — sprite assets are 32×64; we render at 2× = 64×128 px so
 *  the character is clearly visible on a 1080p screen without breaking the
 *  camera-follow math (which setZoom does). */
const AVATAR_DISPLAY_SCALE = 2;

export interface Appearance {
  bodyIdx: number;            // 1..9
  eyesIdx: number;            // 1..7
  hairFamily: number;         // 1..29 (0 = bald)
  hairVariant: number;        // 1..7
  outfitFamily: number;       // 1..33
  outfitVariant: number;      // 1..variants
  headgearFamily: number;     // 0 = none, 1..14
  headgearVariant: number;    // 0..3
  accessoryFamily: number;    // 0 = none, 1..28
  accessoryVariant: number;   // 1..variants
}

export const DEFAULT_APPEARANCE: Appearance = {
  bodyIdx: 1,
  eyesIdx: 1,
  hairFamily: 1,
  hairVariant: 1,
  outfitFamily: 1,
  outfitVariant: 1,
  headgearFamily: 0,
  headgearVariant: 0,
  accessoryFamily: 0,
  accessoryVariant: 0,
};

const ANIMATED_LAYERS: Layer[] = ["body", "eyes", "outfit", "hair", "accessory"];

export class LimezuAvatar {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private layerSprites: Map<Layer, Phaser.GameObjects.Sprite> = new Map();
  private headgearImage: Phaser.GameObjects.Image | null = null;
  private appearance: Appearance;
  private direction: Direction = "down";
  private moving: boolean = false;
  private destroyed = false;

  constructor(scene: Phaser.Scene, x: number, y: number, appearance: Appearance) {
    this.scene = scene;
    this.appearance = { ...appearance };

    this.container = scene.add.container(x, y);
    this.container.setDepth(DEFAULT_DEPTH);
    this.container.setScale(AVATAR_DISPLAY_SCALE);

    // Create empty sprites for each animated layer; they'll be configured
    // once their textures load. Fall back to a visible body texture if
    // others are missing.
    for (const layer of ANIMATED_LAYERS) {
      const spr = scene.add
        .sprite(0, -16, "")
        .setOrigin(0.5, 1)
        .setVisible(false);
      this.layerSprites.set(layer, spr);
      this.container.add(spr);
    }
  }

  /** Lazy-load + configure the texture for one layer. */
  private async ensureLayer(layer: Layer): Promise<void> {
    if (this.destroyed) return;
    // Make sure the manifest fetch has completed before we try to find an
    // entry — otherwise findEntry() throws and the whole load chain dies
    // silently, leaving the sprite at __MISSING.
    try {
      await loadManifest();
    } catch (err) {
      console.warn("[LimezuAvatar] manifest load failed; using fallback only", err);
    }
    let family: number;
    let variant: number;
    switch (layer) {
      case "body":      family = this.appearance.bodyIdx;        variant = 0; break;
      case "eyes":      family = this.appearance.eyesIdx;        variant = 0; break;
      case "hair":      family = this.appearance.hairFamily;     variant = this.appearance.hairVariant;     break;
      case "outfit":    family = this.appearance.outfitFamily;   variant = this.appearance.outfitVariant;   break;
      case "headgear":  family = this.appearance.headgearFamily; variant = this.appearance.headgearVariant; break;
      case "accessory": family = this.appearance.accessoryFamily;variant = this.appearance.accessoryVariant;break;
    }
    if (family <= 0) {
      // "None" — hide the layer.
      if (layer === "headgear" && this.headgearImage) {
        this.headgearImage.setVisible(false);
      } else {
        const spr = this.layerSprites.get(layer);
        spr?.setVisible(false);
      }
      return;
    }

    await loadParts(this.scene, [{ layer, family, variant }]);
    if (this.destroyed) return;

    let key = partKey(layer, family, variant);
    if (!this.scene.textures.exists(key)) {
      // Fallback: load failed (404 / variant doesn't exist on disk) → use the
      // canonical default for this layer so the character never appears
      // transparent. This is what causes the "ghost player" bug if the user's
      // saved appearance points at a non-existent variant.
      console.warn(`[LimezuAvatar] texture missing for ${layer}:${family}:${variant} — falling back to default`);
      const defaultKey = partKey(layer, 1, layer === "hair" || layer === "outfit" || layer === "accessory" ? 1 : 0);
      if (this.scene.textures.exists(defaultKey)) {
        key = defaultKey;
      } else if (layer !== "body") {
        // Last-resort fallback: try just the body so something renders
        return;
      } else {
        return;
      }
    }

    if (layer === "headgear") {
      // The character body sprite is 32×64 but its content bbox is y=20-64
      // (top 20 pixels of the frame are transparent — verified empirically).
      // With sprite at container-local (0, -16) and origin (0.5, 1):
      //   sprite bottom (feet) at container y = -16
      //   character image starts at sprite-local y=20 → container y = -60
      //   character head region is sprite-local y≈20-40 → container y = -60 to -40
      // For the hat to SIT ON the head:
      //   hat bottom should align with sprite-local y≈30 (eyebrow line) →
      //   container y = -16 - (64-30) = -50, but with origin (0.5, 0) anchored
      //   at the TOP of the hat, top y = container y = -60 - 8 = -68
      //   so hat occupies container y = -68 to -36 (covers brow + crown)
      // Hat positioning math: container at body.y + 42 with scale 2; body
      // sprite at local (0, -16) origin (0.5, 1) means sprite renders from
      // local y=-48 (top) to y=-16 (bottom = feet anchor). Within that,
      // LimeZu's character HEAD sits roughly at sprite-local y=22-42 of the
      // 32×64 native frame — i.e. container-local y range ≈ -45 to -25.
      //
      // The 32×32 headgear PNGs draw the helmet in the upper ~20 px of the
      // frame. With origin (0.5, 0) the hat's top edge is the position
      // anchor, so to land the helmet ON the head (bottom of helmet ≈ top
      // of head) the position should be local y ≈ -56.
      //
      // The previous value (-68) was tuned for the OLD avatar offset (body.y
      // + 20) and left the hat floating ~16 px above the head once we moved
      // the avatar down to body.y + 42.
      // Headgear sits ABOVE the hair layer, not on the bare head — LimeZu hair
      // sprites add ~10 px of hair on top of the body's head, so the hat
      // anchor needs to clear the hair top.
      //   Body sprite head top (sprite-local y=32) → world y = body.y - 54
      //   Hair top (sprite-local y≈22)             → world y = body.y - 74
      //   Hat top should sit just above hair top   → world y ≈ body.y - 94
      // With container at body.y + 42 and scale 2 → local y = (-94 - 42)/2 = -68.
      const HAT_LOCAL_Y = -68;
      if (!this.headgearImage) {
        this.headgearImage = this.scene.add.image(0, HAT_LOCAL_Y, key).setOrigin(0.5, 0);
        this.container.add(this.headgearImage);
      } else {
        this.headgearImage.setTexture(key);
        this.headgearImage.y = HAT_LOCAL_Y;
      }
      this.headgearImage.setVisible(true);
    } else {
      const spr = this.layerSprites.get(layer);
      if (!spr) return;
      spr.setTexture(key);
      spr.setFrame(DIRECTION_FRAMES[this.direction]);
      spr.setVisible(true);
    }
  }

  /** Initial async load of every layer in the current appearance. */
  async start(): Promise<void> {
    await Promise.all(ANIMATED_LAYERS.map((l) => this.ensureLayer(l)));
    if (this.appearance.headgearFamily > 0) await this.ensureLayer("headgear");
  }

  setAppearance(appearance: Partial<Appearance>): Promise<void> {
    this.appearance = { ...this.appearance, ...appearance };
    const reload: Layer[] = [];
    for (const l of ANIMATED_LAYERS) reload.push(l);
    if ("headgearFamily" in appearance || "headgearVariant" in appearance) reload.push("headgear");
    return Promise.all(reload.map((l) => this.ensureLayer(l))).then(() => {});
  }

  getAppearance(): Appearance {
    return { ...this.appearance };
  }

  /** Place avatar at world (x, y). y is the "feet" position. */
  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  setDepth(depth: number): void {
    this.container.setDepth(depth);
  }

  setDirection(dir: Direction): void {
    if (this.direction === dir) return;
    this.direction = dir;
    // Always snap to the static preview frame for this direction. (The
    // experimental walk-cycle was cycling into wrong/empty atlas cells —
    // rolled back until per-direction walk frame indices are verified
    // against the LimeZu sprite atlas.)
    const frame = DIRECTION_FRAMES[dir];
    for (const [layer, spr] of this.layerSprites) {
      if (layer === "headgear") continue; // static image, no frames
      if (spr.texture && spr.texture.key !== "__MISSING" && spr.frame.name !== `${frame}`) {
        spr.setFrame(frame);
      }
    }
    // Headgear is a single top-down 32×32 image (LimeZu's free pack has no
    // direction frames). Apply visual approximations so the hat reads
    // correctly per direction:
    //   - DOWN: hat visible, normal position
    //   - UP:   hide hat (would show wrong front-facing brim from behind)
    //   - LEFT: visible, mirrored horizontally + nudged 1px toward facing side
    //   - RIGHT: visible, normal + nudged 1px toward facing side
    if (this.headgearImage && this.appearance.headgearFamily > 0) {
      if (dir === "up") {
        this.headgearImage.setVisible(false);
      } else {
        this.headgearImage.setVisible(true);
        if (dir === "left") {
          this.headgearImage.setFlipX(true);
          this.headgearImage.x = -1;
        } else if (dir === "right") {
          this.headgearImage.setFlipX(false);
          this.headgearImage.x = 1;
        } else { // down
          this.headgearImage.setFlipX(false);
          this.headgearImage.x = 0;
        }
      }
    }
  }

  setMoving(moving: boolean): void {
    if (this.moving === moving) return;
    this.moving = moving;
    if (moving) {
      // Reset walk-cycle so a fresh stride begins on frame 0.
      this.walkFrameIdx = 0;
      this.walkAnimTime = 0;
    } else {
      // Snap back to the static preview frame for the current direction.
      const frame = DIRECTION_FRAMES[this.direction];
      for (const [layer, spr] of this.layerSprites) {
        if (layer === "headgear") continue;
        if (spr.texture && spr.texture.key !== "__MISSING") spr.setFrame(frame);
      }
    }
  }

  // Walk-cycle frame stepper. While moving, we cycle through 6 walk frames
  // per direction at ~10 fps. Each layer (body/eyes/outfit/hair) shares the
  // same atlas grid so the same frame index gives a synchronized pose.
  private walkAnimTime = 0;
  private walkFrameIdx = 0;
  private static readonly WALK_FRAME_MS = 100; // 10 fps walk cadence

  /** Per-frame tick — small bob so the character isn't fully static.
   *  Real walk-frame cycling is disabled (was hitting wrong atlas cells
   *  for left/down, causing "character missing" at those directions). */
  update(_delta: number, time: number = performance.now()): void {
    if (this.destroyed) return;
    const amplitude = this.moving ? 1.5 : 0.5;
    const period = this.moving ? 220 : 700;
    const offset = Math.sin(time / period) * amplitude;
    for (const [layer, spr] of this.layerSprites) {
      if (layer === "headgear") continue;
      spr.y = -16 + offset;
    }
    if (this.headgearImage) this.headgearImage.y = -68 + offset;
  }

  destroy(): void {
    this.destroyed = true;
    this.container.destroy();
    this.layerSprites.clear();
    this.headgearImage = null;
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }
}
